import { useEffect, useCallback } from "react";
import type React from "react";
import BrandIcon from "./BrandIcon";
import { CopilotIcon } from "./CopilotIcon";
import { aiExamples } from "./aiExampleTemplates";
import { FEEDBACK_TEAMS_CHAT_URL } from "../env";
import { CONTRIBUTOR_ACCESS_URL, DOCS_URL, REPOSITORY_URL } from "../productLinks";
import "./ai-showcase.css";

interface AboutDialogProps {
  onClose: () => void;
}

export default function AboutDialog({ onClose }: AboutDialogProps) {
  const resources = [
    {
      id: "docs",
      title: "Documentation",
      description: "Explore product guides and learn what you can do.",
      icon: documentationIcon,
      links: [{ label: "Read documentation", href: DOCS_URL }],
    },
    {
      id: "teams",
      title: "Teams",
      description: "Ask questions, share ideas, and connect with the team.",
      icon: communityIcon,
      links: FEEDBACK_TEAMS_CHAT_URL
        ? [{ label: "Join us on Teams", href: FEEDBACK_TEAMS_CHAT_URL }]
        : [],
    },
    {
      id: "github",
      title: "GitHub",
      description:
        "Explore the code and contribute. Private repository; contributor access required.",
      icon: repositoryIcon,
      links: [
        { label: "View repository", href: REPOSITORY_URL },
        { label: "Request contributor access", href: CONTRIBUTOR_ACCESS_URL },
      ],
    },
  ];
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [handleKeyDown]);

  return (
    <div
      style={overlayStyle}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="about-heading"
    >
      <div style={dialogStyle} onClick={(e) => e.stopPropagation()} data-testid="about-dialog">
        {/* Scrollable content */}
        <div style={bodyStyle}>
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={logoRowStyle}>
              <BrandIcon size={40} />
            </div>
            <h1 id="about-heading" style={headingStyle}>
              Excalidraw for Microsoft 365
            </h1>
            <p style={subtitleStyle}>
              A browser-based collaborative whiteboard for Microsoft employees, built on open-source
              Excalidraw. Turn ideas into sketches, architecture diagrams, and shared visual work.
            </p>
          </div>

          <Section title="Create with AI" titleId="about-ai-heading">
            <div className="ai-agent-path">
              <div className="ai-path-heading">
                <span className="ai-path-icon" aria-hidden="true">
                  <CopilotIcon />
                </span>
                <div>
                  <h3>Create with GitHub Copilot</h3>
                  <p>In the GitHub Copilot CLI or desktop app</p>
                </div>
                <span className="ai-status">Cloud drawings only</span>
              </div>
              <p className="ai-path-description">
                Create new drawings saved online in Excalidraw-MS, or edit drawings already saved
                there.
              </p>
              <div className="ai-home-examples" style={{ marginTop: 12 }}>
                {aiExamples.map((example) => (
                  <article className="ai-home-example" key={example.label}>
                    <img src={example.image} alt={example.alt} />
                    <div>
                      <h3>{example.label}</h3>
                      <p>{example.summary}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </Section>

          <Section title="Draw, save, and work together">
            <p style={sectionNoteStyle}>
              Start in your browser. Cloud storage, sharing, and collaboration require cloud access.
            </p>
            <div style={gridStyle}>
              {features.map((f) => (
                <div key={f.title} style={featureCardStyle}>
                  <div style={featureTitleStyle}>{f.title}</div>
                  <div style={featureDescStyle}>{f.desc}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Tech Stack">
            <div style={techGridStyle}>
              {techStack.map((s) => (
                <div key={s.layer} style={techCardStyle}>
                  <div style={techLayerStyle}>{s.layer}</div>
                  <div style={techDetailStyle}>{s.tech}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Learn more & connect" titleId="about-resources-heading">
            <div style={techGridStyle}>
              {resources.map((resource) => (
                <article
                  key={resource.id}
                  aria-labelledby={`about-resource-${resource.id}`}
                  style={resourceCardStyle}
                >
                  <h3 id={`about-resource-${resource.id}`} style={resourceTitleStyle}>
                    {resource.icon}
                    {resource.title}
                  </h3>
                  <p style={{ ...featureDescStyle, margin: 0 }}>{resource.description}</p>
                  <div style={resourceLinksStyle}>
                    {resource.links.length ? (
                      resource.links.map((link) => (
                        <a
                          key={link.label}
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={textLinkStyle}
                        >
                          {link.label}
                        </a>
                      ))
                    ) : (
                      <span>Community link is unavailable.</span>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </Section>

          {/* Team & Contact */}
          <Section title="">
            <div style={teamSectionStyle}>
              <h3 style={teamTitleStyle}>Team &amp; Contact</h3>
              <div style={teamGridStyle}>
                {team.map((member) => (
                  <div key={member.name} style={{ textAlign: "center", minWidth: 160 }}>
                    {member.photo ? (
                      <img
                        src={member.photo}
                        alt={member.name}
                        style={avatarImgStyle}
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div style={avatarStyle}>{getInitials(member.name)}</div>
                    )}
                    <div style={memberNameStyle}>{member.name}</div>
                    <a href={`mailto:${member.email}`} style={memberEmailStyle}>
                      {member.email}
                    </a>
                  </div>
                ))}
              </div>
              <p style={footerNoteStyle}>FHL 2026 · Powered by GitHub Copilot</p>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Section({
  title,
  titleId,
  children,
}: {
  title: string;
  titleId?: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ marginBottom: 24 }} aria-labelledby={titleId}>
      {title && (
        <h2 id={titleId} style={sectionTitleStyle}>
          {title}
        </h2>
      )}
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

const features = [
  {
    title: "Infinite Canvas",
    desc: "Sketch ideas, connect shapes, and map architecture with the open-source Excalidraw editor.",
  },
  {
    title: "Browser & Files",
    desc: "Keep local work in this browser, import drawing files, and export drawings or images.",
  },
  {
    title: "Cloud Drawings",
    desc: "Save to SharePoint Embedded and return through My Drawings or Shared with Me.",
  },
  {
    title: "Live Collaboration",
    desc: "Work together with live cursors, presence, and synchronized edits on the same canvas.",
  },
  {
    title: "Sharing with Control",
    desc: "Give people view or edit access to a live drawing, or share a read-only snapshot. Sign-in is required.",
  },
  {
    title: "Your Drawing Workflow",
    desc: "Find text on the canvas, use the command palette, and choose light, dark, or system appearance.",
  },
];

const techStack = [
  { layer: "Frontend", tech: "React, TypeScript, Vite, @excalidraw/excalidraw" },
  { layer: "Backend", tech: "Node.js, Express, TypeScript, ws" },
  { layer: "Real-time", tech: "WebSocket proxy → excalidraw-room (Socket.IO)" },
  { layer: "Storage", tech: "SharePoint Embedded + Azure Cosmos DB" },
  { layer: "Auth", tech: "Microsoft Entra ID, MSAL, JWT, OBO" },
  {
    layer: "MCP Server",
    tech: "Hosted MCP (TypeScript); browser WebMCP; local tools (Python, FastMCP)",
  },
  { layer: "Infra", tech: "Azure SWA, Container Apps, Bicep IaC" },
];

const team = [
  { name: "Eyal Carmi", email: "eyal.carmi@microsoft.com", photo: "/team-eyal.jpg" },
  { name: "Jason Zhang", email: "jasonzhang@microsoft.com", photo: "/team-jason.jpg" },
];

const documentationIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M15 3h6v6m0-6L11 13M10 5H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" />
  </svg>
);

const communityIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="9" cy="8" r="3" />
    <path d="M3 20v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1" />
    <circle cx="17" cy="9" r="2.5" />
    <path d="M17 14a4 4 0 0 1 4 4v2" />
  </svg>
);

const repositoryIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="6" cy="5" r="2.5" />
    <circle cx="6" cy="19" r="2.5" />
    <circle cx="18" cy="5" r="2.5" />
    <path d="M6 7.5v9M18 7.5v1a5 5 0 0 1-5 5H6" />
  </svg>
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const EXCALIDRAW_FONT =
  "Assistant, system-ui, BlinkMacSystemFont, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif";

const MODAL_SHADOW = "var(--exms-shadow-modal)";

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 10000,
  background: "var(--exms-overlay-bg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};

const dialogStyle: React.CSSProperties = {
  boxSizing: "border-box",
  background: "var(--island-bg-color)",
  borderRadius: 12,
  border: "1px solid var(--dialog-border-color)",
  boxShadow: MODAL_SHADOW,
  fontFamily: EXCALIDRAW_FONT,
  width: "100%",
  maxWidth: 720,
  maxHeight: "85vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const bodyStyle: React.CSSProperties = {
  overflowY: "auto",
  minHeight: 0,
  padding: 24,
  flex: 1,
};

const logoRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 8,
};

const headingStyle: React.CSSProperties = {
  fontSize: "1.5rem",
  fontWeight: 700,
  color: "var(--text-primary-color)",
  margin: "0 0 6px",
};

const subtitleStyle: React.CSSProperties = {
  fontSize: "0.9rem",
  color: "var(--text-primary-color)",
  maxWidth: 520,
  margin: "0 auto",
  lineHeight: 1.5,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: "0.95rem",
  fontWeight: 600,
  marginBottom: 10,
  color: "var(--text-primary-color)",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))",
  gap: 10,
};

const featureCardStyle: React.CSSProperties = {
  padding: 14,
  backgroundColor: "var(--color-surface-low)",
  borderRadius: 8,
  border: "1px solid var(--dialog-border-color)",
};

const featureTitleStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: "0.8rem",
  color: "var(--text-primary-color)",
  marginBottom: 4,
};

const featureDescStyle: React.CSSProperties = {
  fontSize: "0.75rem",
  color: "var(--text-primary-color)",
  lineHeight: 1.5,
};

const techGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(180px, 100%), 1fr))",
  gap: 10,
};

const techCardStyle: React.CSSProperties = {
  padding: 12,
  backgroundColor: "var(--color-surface-low)",
  borderRadius: 8,
  border: "1px solid var(--dialog-border-color)",
};

const techLayerStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: "0.75rem",
  color: "var(--color-primary)",
  marginBottom: 3,
};

const techDetailStyle: React.CSSProperties = {
  fontSize: "0.75rem",
  color: "var(--text-primary-color)",
  overflowWrap: "anywhere",
};

const sectionNoteStyle: React.CSSProperties = {
  margin: "0 0 12px",
  fontSize: "0.8rem",
  lineHeight: 1.5,
  color: "var(--text-primary-color)",
};

const resourceCardStyle: React.CSSProperties = {
  ...featureCardStyle,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const resourceTitleStyle: React.CSSProperties = {
  ...featureTitleStyle,
  display: "flex",
  alignItems: "center",
  gap: 8,
  margin: 0,
  fontSize: "0.875rem",
};

const resourceLinksStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 6,
  marginTop: "auto",
  fontSize: "0.8rem",
  lineHeight: 1.5,
  color: "var(--color-text-secondary)",
  overflowWrap: "anywhere",
};

const textLinkStyle: React.CSSProperties = {
  color: "var(--color-primary)",
  textUnderlineOffset: 3,
};

const teamSectionStyle: React.CSSProperties = {
  padding: 20,
  backgroundColor: "var(--color-surface-low)",
  borderRadius: 10,
  border: "1px solid var(--dialog-border-color)",
};

const teamTitleStyle: React.CSSProperties = {
  fontSize: "0.9rem",
  fontWeight: 600,
  color: "var(--text-primary-color)",
  marginBottom: 14,
  textAlign: "center",
};

const teamGridStyle: React.CSSProperties = {
  display: "flex",
  gap: 24,
  justifyContent: "center",
  flexWrap: "wrap",
};

const avatarStyle: React.CSSProperties = {
  width: 52,
  height: 52,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--color-primary)",
  color: "var(--color-on-accent)",
  fontSize: 18,
  fontWeight: 600,
  margin: "0 auto 6px",
};

const avatarImgStyle: React.CSSProperties = {
  width: 52,
  height: 52,
  borderRadius: "50%",
  objectFit: "cover",
  margin: "0 auto 6px",
  display: "block",
};

const memberNameStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: "0.85rem",
  color: "var(--text-primary-color)",
};

const memberEmailStyle: React.CSSProperties = {
  fontSize: "0.75rem",
  color: "var(--color-primary)",
  textDecoration: "none",
};

const footerNoteStyle: React.CSSProperties = {
  textAlign: "center",
  color: "var(--text-primary-color)",
  fontSize: "0.75rem",
  marginTop: 14,
  marginBottom: 0,
};
